import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FolderPlus, BookOpen, Globe, Lock, Trash2, Edit2, Plus, X, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getMyCollections, createCollection, deleteCollection, getPublicCollections } from '../services/api';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

function CollectionsPage() {
  const { isAuthenticated } = useAuth();
  const [myCollections, setMyCollections] = useState([]);
  const [publicCollections, setPublicCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newCollection, setNewCollection] = useState({ name: '', description: '', is_public: true });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchCollections();
  }, [isAuthenticated]);

  const fetchCollections = async () => {
    try {
      const publicRes = await getPublicCollections(12);
      setPublicCollections(publicRes.data);
      
      if (isAuthenticated) {
        const myRes = await getMyCollections();
        setMyCollections(myRes.data);
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCollection.name.trim()) {
      toast.error('Please enter a collection name');
      return;
    }

    setCreating(true);
    try {
      await createCollection(newCollection);
      toast.success('Collection created!');
      setShowCreate(false);
      setNewCollection({ name: '', description: '', is_public: true });
      fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create collection');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    
    try {
      await deleteCollection(id);
      toast.success('Collection deleted');
      setMyCollections(myCollections.filter(c => c.id !== id));
    } catch (error) {
      toast.error('Failed to delete collection');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 pt-8 page-padding" data-testid="collections-page">
      <SEO title="Collections" description="Create and explore comic collections" />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-teko text-gradient">Collections</h1>
            <p className="text-[var(--text-secondary)]">Create your own reading lists</p>
          </div>
          
          {isAuthenticated && (
            <button
              onClick={() => setShowCreate(true)}
              className="btn-hotrod flex items-center gap-2"
              data-testid="create-collection-btn"
            >
              <FolderPlus size={20} />
              New Collection
            </button>
          )}
        </div>

        {/* My Collections */}
        {isAuthenticated && (
          <section className="mb-12">
            <h2 className="text-2xl font-teko mb-4 flex items-center gap-2">
              <BookOpen className="text-[var(--primary)]" size={24} />
              My Collections
            </h2>
            
            {myCollections.length === 0 ? (
              <div className="card-hotrod p-8 text-center">
                <FolderPlus size={48} className="mx-auto mb-4 text-[var(--text-secondary)] opacity-50" />
                <p className="text-[var(--text-secondary)] mb-4">You haven't created any collections yet</p>
                <button onClick={() => setShowCreate(true)} className="btn-outline">
                  Create Your First Collection
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myCollections.map((collection) => (
                  <Link
                    key={collection.id}
                    to={`/collections/${collection.id}`}
                    className="card-hotrod p-5 group hover:border-[var(--primary)]/50 transition-all"
                    data-testid={`collection-${collection.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {collection.is_public ? (
                          <Globe size={16} className="text-green-500" />
                        ) : (
                          <Lock size={16} className="text-yellow-500" />
                        )}
                        <h3 className="font-teko text-xl group-hover:text-[var(--primary)] transition-colors">
                          {collection.name}
                        </h3>
                      </div>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          handleDelete(collection.id, collection.name);
                        }}
                        className="p-1.5 hover:bg-red-500/20 rounded-lg text-[var(--text-secondary)] hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    
                    {collection.description && (
                      <p className="text-sm text-[var(--text-secondary)] line-clamp-2 mb-3">
                        {collection.description}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[var(--text-secondary)]">
                        {collection.comic_count} {collection.comic_count === 1 ? 'comic' : 'comics'}
                      </span>
                      <ChevronRight size={16} className="text-[var(--text-secondary)] group-hover:text-[var(--primary)] transition-colors" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Public Collections */}
        <section>
          <h2 className="text-2xl font-teko mb-4 flex items-center gap-2">
            <Globe className="text-green-500" size={24} />
            Discover Collections
          </h2>
          
          {publicCollections.length === 0 ? (
            <div className="card-hotrod p-8 text-center">
              <p className="text-[var(--text-secondary)]">No public collections yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {publicCollections.map((collection) => (
                <Link
                  key={collection.id}
                  to={`/collections/${collection.id}`}
                  className="card-hotrod p-5 group hover:border-[var(--primary)]/50 transition-all"
                >
                  <h3 className="font-teko text-xl mb-2 group-hover:text-[var(--primary)] transition-colors">
                    {collection.name}
                  </h3>
                  
                  <p className="text-xs text-[var(--text-secondary)] mb-3">
                    by {collection.username}
                  </p>
                  
                  {collection.description && (
                    <p className="text-sm text-[var(--text-secondary)] line-clamp-2 mb-3">
                      {collection.description}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[var(--text-secondary)]">
                      {collection.comic_count} comics
                    </span>
                    <ChevronRight size={16} className="text-[var(--text-secondary)] group-hover:text-[var(--primary)] transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-teko text-2xl">Create Collection</h2>
              <button onClick={() => setShowCreate(false)} className="p-2 hover:bg-[var(--background)] rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  value={newCollection.name}
                  onChange={(e) => setNewCollection({...newCollection, name: e.target.value})}
                  placeholder="My Reading List"
                  className="w-full px-4 py-3 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:border-[var(--primary)]"
                  maxLength={50}
                  data-testid="collection-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={newCollection.description}
                  onChange={(e) => setNewCollection({...newCollection, description: e.target.value})}
                  placeholder="What's this collection about?"
                  rows={3}
                  className="w-full px-4 py-3 bg-[var(--background)] border border-[var(--border)] rounded-lg focus:outline-none focus:border-[var(--primary)] resize-none"
                  maxLength={200}
                  data-testid="collection-desc-input"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={newCollection.is_public}
                  onChange={(e) => setNewCollection({...newCollection, is_public: e.target.checked})}
                  className="w-4 h-4 rounded border-[var(--border)] bg-[var(--background)]"
                />
                <label htmlFor="is_public" className="text-sm">
                  Make this collection public
                </label>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="flex-1 py-3 border border-[var(--border)] rounded-lg hover:bg-[var(--background)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 py-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 transition-colors disabled:opacity-50"
                  data-testid="create-collection-submit"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CollectionsPage;
